import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Empty, Menu, Dropdown } from "antd";
import { ChevronIcon } from "../svgV2/Chevron";
import CustomInputBox from "./CustomInputBox";
import { CheckIcon } from "../svgV2/Check";
import CustomSearchBox from "./CustomSearchBox";
import { CrossIcon } from "../svgV2/CrossIcon";

type CustomSelectProps = {
  options?: any;
  value?: any;
  onDropdownVisibleChange?: any;
  onChange?: (e) => void;
  onMouseOver?: (e) => void;
  placeholder?: React.ReactNode;
  border?: boolean;
  backgroundColor?: string;
  disabled?: boolean;

  name?: string;

  wrapperClassName?: string;
  innerClassName?: string;
  onFocusChange?: (isTrue, e) => void;
  showBorderOnlyOnFocused?: boolean;
  defaultOpen?: boolean;
  showSearch?: boolean;
  mode?: "multiple" | "tags";
  customDropDownRender?: (menu) => React.ReactNode;
  group?: boolean;
  isSearchable?: boolean;
  formState?: any;
  dropdownStyle?: React.CSSProperties;
};

const CustomSelect = ({
  options = [],
  value,
  onDropdownVisibleChange,
  onChange,
  placeholder,
  border = true,
  onMouseOver,
  isSearchable = false,
  backgroundColor = "bg-white",
  disabled,
  name,
  mode,
  wrapperClassName = "h-[30px]",
  innerClassName,
  onFocusChange,
  showBorderOnlyOnFocused = false,
  defaultOpen = false,
  showSearch = false,
  customDropDownRender,
  group = false,
  formState = {},
  dropdownStyle = {},
}: CustomSelectProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(defaultOpen);
  const [search, setSearch] = useState("");
  // const selectRef = useRef(null);

  const { errors } = formState;
  const showError = errors?.[name];
  const errorText = errors?.[name]?.message;

  useEffect(() => {
    setSearch("");
  }, [isDropdownOpen]);

  const wrapperClasses = [
    "transition-shadow duration-300",
    border && "border",
    showBorderOnlyOnFocused && isDropdownOpen && "border",
    isDropdownOpen && "border",
    showError
      ? "border border-error-600 shadow-input-error"
      : !border && !showBorderOnlyOnFocused
      ? ""
      : isDropdownOpen
      ? "border-primary-500 shadow-input-focus"
      : disabled
      ? "border-grey-200"
      : "border-[#C0CBD7]",
    "bg-white rounded-[4px]",
    "flex items-center w-full",
    wrapperClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !search) return options;
    return group
      ? options
          .map((groupItem) => ({
            ...groupItem,
            options: groupItem.options.filter(
              (option) =>
                option.label?.toLowerCase().includes(search.toLowerCase()) ||
                option.value?.toLowerCase().includes(search.toLowerCase())
            ),
          }))
          .filter((groupItem) => groupItem.options.length > 0)
      : options.filter(
          (option) =>
            option.label?.toLowerCase().includes(search.toLowerCase()) ||
            option.value?.toLowerCase().includes(search.toLowerCase())
        );
  }, [options, search, isSearchable, group]);

  return (
    <>
      <div className={wrapperClasses}>
        <div className="rounded-[4px] w-full bg-transparent">
          <Select
            // ref={selectRef}
            menuItemSelectedIcon={
              <CheckIcon className="text-primary-700 h-5 w-5" />
            }
            showSearch={showSearch}
            className={`w-full flex items-center custom-select-input ${innerClassName}`}
            bordered={false}
            onFocus={(e) => {
              setIsDropdownOpen(true);
              if (typeof onFocusChange === "function") onFocusChange(true, e);
            }}
            onBlur={(e) => {
              // setTimeout(() => setIsDropdownOpen(false), 200);
              setSearch("");
              if (typeof onFocusChange === "function") {
                console.log("onFocusChange", onFocusChange);
              }
            }}
            getPopupContainer={(trigger) => trigger.parentElement}
            dropdownStyle={dropdownStyle}
            autoFocus={showSearch && defaultOpen}
            mode={mode}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            suffixIcon={
              <div
                onMouseDown={(e) => {
                  e.preventDefault();
                  // selectRef.current?.focus();
                  setIsDropdownOpen(!isDropdownOpen);
                }}
              >
                <ChevronIcon
                  className={`text-gray-500 font-bold ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            }
            open={isDropdownOpen}
            popupClassName={`truncate max-h-[320px] ${
              border ? "border border-gray-100" : ""
            } bg-white rounded-lg`}
            dropdownRender={(menu) =>
              customDropDownRender ? (
                <>{customDropDownRender(menu)}</>
              ) : (
                <div
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  {isSearchable && (
                    <div
                      className="p-2"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <CustomSearchBox
                        className={`h-[30px] ${
                          search ? "border-primary-300" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        style={{ borderRadius: "8px" }}
                        search={search}
                        suffix={
                          <button
                            className="h-7"
                            onClick={(e) => {
                              setSearch("");
                              // setIsDropdownOpen(true);
                              // e.stopPropagation();
                              // e.preventDefault();
                            }}
                          >
                            <CrossIcon className="text-gray-500" />
                          </button>
                        }
                        setSearch={(s) => setSearch(s)}
                      />
                    </div>
                  )}
                  {menu}
                </div>
              )
            }
            onDropdownVisibleChange={(open) => {
              setIsDropdownOpen(open);
              if (typeof onDropdownVisibleChange === "function") {
                onDropdownVisibleChange(open);
              }
            }}
            onSelect={(value, option) => {
              onChange(value);
              setIsDropdownOpen(true); // Prevent dropdown from closing
            }}
            filterOption={(input, option) => {
              const lowerInput = input?.toLowerCase();
              const labelMatch =
                option.label &&
                `${option.label}`.toLowerCase().includes(lowerInput);
              const valueMatch =
                option.value &&
                `${option.value}`.toLowerCase().includes(lowerInput);
              return labelMatch || valueMatch;
            }}
          >
            {filteredOptions.length > 0 ? (
              group ? (
                <>
                  {filteredOptions.map((group) => (
                    <Select.OptGroup
                      label={
                        <p className="out-300-14 text-gray-500 px-3 py-2 border-b border-gray-200">
                          {group.title}
                        </p>
                      }
                      key={group.id}
                    >
                      {group.options.map((option) => (
                        <Select.Option
                          key={option.id}
                          value={option.value}
                          label={option.label || option.value}
                          className="py-2.5"
                        >
                          {option.render(value)}
                        </Select.Option>
                      ))}
                    </Select.OptGroup>
                  ))}
                </>
              ) : (
                filteredOptions.map((option) => (
                  <Select.Option
                    key={option.id}
                    value={option.value}
                    label={option.label || option.value}
                    className="py-2.5"
                    onMouseOver={onMouseOver}
                  >
                    {option.render(value)}
                  </Select.Option>
                ))
              )
            ) : null}
          </Select>
        </div>
      </div>
      {showError && (
        <div className="mt-1.5 out-300-12 text-left text-error-600">
          {errorText}
        </div>
      )}
    </>
  );
};

export const MultiSelect = ({
  placeholder,
  options,
  onOpenChange,
  autoFocus = false,
}: {
  placeholder: string;
  options: any;
  onOpenChange?: (open) => void;
  autoFocus?: boolean;
}) => {
  const [search, setSearch] = useState("");
  const [filterOptions, setFilterOption] = useState(options);
  const [open, setOpen] = useState(false);

  const handleOpenChange = (flag: boolean) => {
    console.log(flag, open, "flafffffff");
    setOpen(flag);
    if (onOpenChange && typeof onOpenChange === "function") {
      onOpenChange(flag);
    }
  };

  useEffect(() => {
    const filters = options?.filter(
      (option) =>
        option && option.label?.toLowerCase().includes(search.toLowerCase())
    );
    setFilterOption([...filters]);
  }, [search, options]);

  useEffect(() => {
    if (autoFocus) {
      handleOpenChange(true);
      // setOpen(true);
    }
  }, [autoFocus]);
  return (
    <Dropdown
      trigger={["click"]}
      onOpenChange={handleOpenChange}
      open={open}
      dropdownRender={() => (
        <Menu
          className=" rounded-[4px] w-full  shadow-dropdown max-h-[200px] overflow-y-scroll"
          //selectable
        >
          {filterOptions?.length > 0 ? (
            filterOptions.map((option) => {
              return (
                <Menu.Item
                  className=" py-2.5 px-4 rounded-lg"
                  key={option.id}
                  // onClick={() => {
                  //   option.onClick(option.value);
                  // }}
                >
                  {option.render(setSearch)}
                </Menu.Item>
              );
            })
          ) : (
            <div className="py-2">
              <Empty description="No users" />
            </div>
          )}
        </Menu>
      )}
      menu={{ items: filterOptions, onClick: () => console.log("Hello") }}
      placement="bottom"
    >
      <CustomInputBox
        placeholder={placeholder}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        autoFocus={autoFocus}
      />
    </Dropdown>
  );
};

export default CustomSelect;
